import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, Users, Eye, TrendingUp, Globe, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface RealTimeData {
  currentPageViews: number;
  recentEvents: number;
  topPages: Array<{ page: string; views: number }>;
}

const RealTimeMetrics: React.FC = () => {
  const [realTimeData, setRealTimeData] = useState<RealTimeData>({
    currentPageViews: 0,
    recentEvents: 0,
    topPages: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRealTimeData = async () => {
      try {
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

        // Page views in last hour
        const { count: currentPageViews } = await supabase
          .from('analytics_events')
          .select('*', { count: 'exact', head: true })
          .eq('event_type', 'page_view')
          .gte('created_at', oneHourAgo);

        // Recent events in last 5 minutes
        const { count: recentEvents } = await supabase
          .from('analytics_events')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', fiveMinutesAgo);

        // Top pages in last hour
        const { data: topPagesData } = await supabase
          .from('analytics_events')
          .select('page_path')
          .eq('event_type', 'page_view')
          .gte('created_at', oneHourAgo)
          .not('page_path', 'is', null);

        const topPages = (topPagesData || [])
          .reduce((acc, event) => {
            const page = event.page_path || 'Unknown';
            acc[page] = (acc[page] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

        const topPagesArray = Object.entries(topPages)
          .map(([page, views]) => ({ page, views }))
          .sort((a, b) => b.views - a.views)
          .slice(0, 5);

        setRealTimeData({
          currentPageViews: currentPageViews || 0,
          recentEvents: recentEvents || 0,
          topPages: topPagesArray,
        });
      } catch (error) {
        console.error('Error fetching real-time data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRealTimeData();
    const interval = setInterval(fetchRealTimeData, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Real-time metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Oldalnézetek (1h)</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{realTimeData.currentPageViews}</div>
            <Badge variant="outline" className="mt-1">
              Utolsó óra
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Események (5p)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{realTimeData.recentEvents}</div>
            <Badge variant="outline" className="mt-1">
              Utolsó 5 perc
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Frissítés</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              {new Date().toLocaleTimeString('hu-HU')}
            </div>
            <Badge variant="outline" className="mt-1">
              30s frissítés
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Top pages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Top oldalak (utolsó óra)
          </CardTitle>
          <CardDescription>
            Legtöbbet megtekintett oldalak az elmúlt órában
          </CardDescription>
        </CardHeader>
        <CardContent>
          {realTimeData.topPages.length > 0 ? (
            <div className="space-y-3">
              {realTimeData.topPages.map((page, index) => (
                <div key={page.page} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center">
                      {index + 1}
                    </Badge>
                    <span className="font-medium text-sm truncate max-w-[200px]">
                      {page.page === '/' ? 'Főoldal' : page.page}
                    </span>
                  </div>
                  <Badge variant="secondary">
                    {page.views} nézet
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4">
              Nincs adat az elmúlt órához
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RealTimeMetrics;